import { useState } from 'react';

const GallerySection = () => {
  const [showAll, setShowAll] = useState(false);

  // Gallery images - Tanzania Digital Awards event photos
  const allImages = [
    { id: 1, src: '/assets/webpage/TDA2024_182.jpg', alt: 'TDA Award Ceremony' },
    { id: 2, src: '/assets/webpage/1.png', alt: 'TDA Event' },
    { id: 3, src: '/assets/webpage/2.png', alt: 'TDA Conference' },
    { id: 4, src: '/assets/webpage/8.jpg', alt: 'TDA Winners' },
    { id: 5, src: '/assets/webpage/9.png', alt: 'TDA Celebration' },
    { id: 6, src: '/assets/webpage/10.jpeg', alt: 'TDA Stage' },
    { id: 7, src: '/assets/webpage/12.jpg', alt: 'TDA Gala' },
    { id: 8, src: '/assets/posters/_YTE3084.jpg', alt: 'TDA Event Photo' },
    { id: 9, src: '/assets/posters/_YTE3069.jpg', alt: 'TDA Venue' },
    { id: 10, src: '/assets/posters/_BYT7100.jpg', alt: 'TDA Attendees' },
    { id: 11, src: '/assets/posters/carousel1.jpg', alt: 'TDA Highlights' },
    { id: 12, src: '/assets/posters/carousel2.jpg', alt: 'TDA Moments' },
    { id: 13, src: '/assets/posters/carousel3.jpg', alt: 'TDA Gallery' },
    { id: 14, src: '/assets/webpage/05.png', alt: 'TDA Digital Excellence' },
    { id: 15, src: '/assets/webpage/06.png', alt: 'TDA Innovation' },
    { id: 16, src: '/assets/webpage/07.png', alt: 'TDA Awards Night' },
    { id: 17, src: '/assets/posters/1633192181.jpg', alt: 'TDA Ceremony' },
    { id: 18, src: '/assets/posters/Abigail-Chams.jpg', alt: 'TDA Professional Event' }
  ];

  // Show only 15 images initially on mobile (5 rows x 3 images)
  const displayedImages = showAll ? allImages : allImages.slice(0, 15);
  const hasMoreImages = allImages.length > 15;

  return (
    <section 
      className="py-24 bg-white" 
      style={{ 
        backgroundColor: '#ffffff', 
        padding: '6rem 0' 
      }}
    >
      <div 
        className="container mx-auto px-4" 
        style={{ maxWidth: '1600px' }}
      >
        {/* Section Header */}
        <div 
          className="text-center mb-16" 
          style={{ marginBottom: '4rem' }}
        >
          <h2 
            className="text-4xl md:text-5xl font-bold text-gray-900 mb-4" 
            style={{ 
              fontSize: 'clamp(2rem, 4vw, 3rem)',
              fontWeight: '700', 
              color: '#111827',
              marginBottom: '1rem'
            }}
          >
            Event Gallery
          </h2>
          <p 
            className="text-gray-600 text-lg max-w-2xl mx-auto" 
            style={{ 
              color: '#6b7280', 
              fontSize: '1.125rem',
              maxWidth: '42rem',
              margin: '0 auto'
            }}
          >
            Moments captured from Tanzania Digital Awards celebrations
          </p>
        </div>

        {/* Desktop Gallery - Artistic 2-row layout with horizontal scroll */}
        <div className="hidden md:block">
          <div 
            className="gallery-desktop-container"
            style={{
              overflowX: 'auto',
              overflowY: 'hidden',
              paddingBottom: '1rem',
              scrollSnapType: 'x proximity',
              WebkitOverflowScrolling: 'touch'
            }}
          >
            <style>{`
              .gallery-desktop-container::-webkit-scrollbar {
                height: 10px;
              }
              
              .gallery-desktop-container::-webkit-scrollbar-track {
                background: #e5e7eb;
                border-radius: 10px;
              }
              
              .gallery-desktop-container::-webkit-scrollbar-thumb {
                background: #ffba17;
                border-radius: 10px;
              }
              
              .gallery-desktop-container::-webkit-scrollbar-thumb:hover {
                background: #ea580c;
              }
            `}</style>
            
            {/* Two rows with artistic arrangement */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', minWidth: 'max-content' }}>
              {/* Row 1 - Varied sizes */}
              <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-end' }}>
                {allImages.slice(0, Math.ceil(allImages.length / 2)).map((image, index) => {
                  // Artistic sizing - vary heights
                  const heights = ['200px', '280px', '240px', '260px', '220px', '300px', '240px', '280px', '220px'];
                  const height = heights[index % heights.length];
                  
                  return (
                    <div
                      key={image.id}
                      className="gallery-item"
                      style={{
                        height: height,
                        minWidth: '280px',
                        maxWidth: '350px',
                        flex: '0 0 auto',
                        borderRadius: '1rem',
                        overflow: 'hidden',
                        boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
                        transition: 'all 0.3s ease',
                        cursor: 'pointer'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'scale(1.05)';
                        e.currentTarget.style.boxShadow = '0 10px 30px rgba(0,0,0,0.2)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'scale(1)';
                        e.currentTarget.style.boxShadow = '0 4px 15px rgba(0,0,0,0.1)';
                      }}
                    >
                      <img
                        src={image.src}
                        alt={image.alt}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover'
                        }}
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          target.src = '/assets/logo/logo2.png';
                        }}
                      />
                    </div>
                  );
                })}
              </div>

              {/* Row 2 - Varied sizes (offset) */}
              <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-start', paddingLeft: '100px' }}>
                {allImages.slice(Math.ceil(allImages.length / 2)).map((image, index) => {
                  // Artistic sizing - vary heights
                  const heights = ['260px', '220px', '300px', '240px', '280px', '220px', '260px', '240px', '280px'];
                  const height = heights[index % heights.length];
                  
                  return (
                    <div
                      key={image.id}
                      className="gallery-item"
                      style={{
                        height: height,
                        minWidth: '280px',
                        maxWidth: '350px',
                        flex: '0 0 auto',
                        borderRadius: '1rem',
                        overflow: 'hidden',
                        boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
                        transition: 'all 0.3s ease',
                        cursor: 'pointer'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'scale(1.05)';
                        e.currentTarget.style.boxShadow = '0 10px 30px rgba(0,0,0,0.2)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'scale(1)';
                        e.currentTarget.style.boxShadow = '0 4px 15px rgba(0,0,0,0.1)';
                      }}
                    >
                      <img
                        src={image.src}
                        alt={image.alt}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover'
                        }}
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          target.src = '/assets/logo/logo2.png';
                        }}
                      />
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        {/* Mobile Gallery - 3 per row with "See More" */}
        <div className="block md:hidden">
          <div 
            className="grid grid-cols-3 gap-3"
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              gap: '0.75rem'
            }}
          >
            {displayedImages.map((image) => (
              <div
                key={image.id}
                style={{
                  aspectRatio: '1',
                  borderRadius: '0.75rem',
                  overflow: 'hidden',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                }}
              >
                <img
                  src={image.src}
                  alt={image.alt}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover'
                  }}
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.src = '/assets/logo/logo2.png';
                  }}
                />
              </div>
            ))}
          </div>

          {/* See More Button */}
          {hasMoreImages && !showAll && (
            <div className="text-center mt-8">
              <button
                onClick={() => setShowAll(true)}
                className="inline-flex items-center px-8 py-4 bg-blue-900 text-white rounded-full font-semibold text-lg hover:bg-blue-800 transition-colors duration-300 group"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  padding: '1rem 2rem',
                  backgroundColor: '#0f1b3c',
                  color: 'white',
                  borderRadius: '9999px',
                  fontWeight: '600',
                  fontSize: '1rem',
                  border: 'none',
                  cursor: 'pointer',
                  transition: 'background-color 0.3s ease'
                }}
              >
                See More Photos
                <i className="fa-solid fa-arrow-down ml-3 group-hover:translate-y-1 transition-transform duration-300" style={{ marginLeft: '0.75rem' }}></i>
              </button>
            </div>
          )}

          {/* Show Less Button */}
          {showAll && (
            <div className="text-center mt-8">
              <button
                onClick={() => setShowAll(false)}
                className="inline-flex items-center px-8 py-4 bg-gray-700 text-white rounded-full font-semibold text-lg hover:bg-gray-600 transition-colors duration-300 group"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  padding: '1rem 2rem',
                  backgroundColor: '#374151',
                  color: 'white',
                  borderRadius: '9999px',
                  fontWeight: '600',
                  fontSize: '1rem',
                  border: 'none',
                  cursor: 'pointer',
                  transition: 'background-color 0.3s ease'
                }}
              >
                Show Less
                <i className="fa-solid fa-arrow-up ml-3 group-hover:-translate-y-1 transition-transform duration-300" style={{ marginLeft: '0.75rem' }}></i>
              </button>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default GallerySection;
